import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { trackCompleteRegistration } from "@/components/pivotbox/metaPixel";
import { gaSignUp } from "@/components/pivotbox/gaEvents";

const archetypeMeta = {
  seeker: { label: "Seeker", color: "#ef4444" },
  evolver: { label: "Evolver", color: "#22c55e" },
  aspirer: { label: "Aspirer", color: "#f59e0b" },
  navigator: { label: "Navigator", color: "#3b82f6" },
};

const baseQuestions = [
  { text: "Over the past several months, the need to leave has felt more urgent to me than figuring out exactly what I will do next.", type: "seeker", reverse: false },
  { text: "I would give up stability and a steady paycheck to be my own boss.", type: "seeker", reverse: false },
  { text: "Even if I were offered a significant promotion or salary increase, it would not change my decision to leave corporate employment.", type: "seeker", reverse: false },
  { text: "Not knowing what comes next does not scare me as much as the thought of staying.", type: "seeker", reverse: false },
  { text: "I would not leave my job without a clear plan for what comes next.", type: "seeker", reverse: true },
  { text: "I would choose a meaningful business over a more profitable one.", type: "evolver", reverse: false },
  { text: "I would build my business around a problem I have personally lived through or seen closely, not just any market gap.", type: "evolver", reverse: false },
  { text: "I would turn down a more scalable business opportunity if it required me to operate in a way that conflicts with my values.", type: "evolver", reverse: false },
  { text: "I have turned down or will turn down opportunities that felt financially attractive but required me to compromise on how I work.", type: "evolver", reverse: false },
  { text: "If a business opportunity is financially strong, I would pursue it even if it does not connect to anything I personally care about.", type: "evolver", reverse: true },
  { text: "I want to build a company large enough that it eventually operates without my direct involvement — even if that means giving up day-to-day control over how things are done.", type: "aspirer", reverse: false },
  { text: "The opportunity to make a lot of money is a major reason I want to start a business.", type: "aspirer", reverse: false },
  { text: "I believe in my own vision for a big business, even when others are not yet convinced.", type: "aspirer", reverse: false },
  { text: "I would rather build a business that captures significant market share than one that stays small and personally meaningful to me.", type: "aspirer", reverse: false },
  { text: "I would rather build a small business that I fully control than a large company that requires outside investors and a team I have to manage.", type: "aspirer", reverse: true },
].map((question, index) => ({ ...question, id: `question-${index + 1}` }));

const shuffleQuestions = (items) => {
  const cloned = [...items];

  for (let index = cloned.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [cloned[index], cloned[randomIndex]] = [cloned[randomIndex], cloned[index]];
  }

  return cloned;
};

const answerLabels = [
  { value: 1, label: "Not at all like me" },
  { value: 2, label: "Not really like me" },
  { value: 3, label: "Somewhat like me" },
  { value: 4, label: "Mostly like me" },
  { value: 5, label: "Completely like me" },
];

// LocalStorage key for in-progress S0 assessment. Bumped when question set changes.
const S0_PROGRESS_KEY = "pivotbox:s0-progress:v4";

function loadStoredProgress() {
  try {
    const raw = typeof window !== "undefined" && window.localStorage
      ? window.localStorage.getItem(S0_PROGRESS_KEY)
      : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.answers || typeof parsed.answers !== "object") return null;
    if (typeof parsed.currentIndex !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearStoredProgress() {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.removeItem(S0_PROGRESS_KEY);
    }
  } catch {
    /* ignore */
  }
}

export default function FounderSignalTestSection({ compact = false } = {}) {
  const navigate = useNavigate();
  // Hydrate from localStorage so a refresh/back-button mid-assessment restores
  // both answers AND the shuffled question order — otherwise a re-shuffle on
  // mount would mismatch saved answer keys with the visible question sequence.
  const stored = (typeof window !== "undefined") ? loadStoredProgress() : null;
  const [questions] = useState(() => {
    if (stored?.questionIds && Array.isArray(stored.questionIds) && stored.questionIds.length === baseQuestions.length) {
      const byId = Object.fromEntries(baseQuestions.map((q) => [q.id, q]));
      const restored = stored.questionIds.map((id) => byId[id]).filter(Boolean);
      if (restored.length === baseQuestions.length) return restored;
    }
    return shuffleQuestions(baseQuestions);
  });
  const [currentIndex, setCurrentIndex] = useState(stored?.currentIndex ?? 0);
  const [answers, setAnswers] = useState(stored?.answers ?? {});
  const [slideDir, setSlideDir] = useState("idle");
  const [showModal, setShowModal] = useState(false);
  const [modalName, setModalName] = useState("");
  const [modalMobile, setModalMobile] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Persist answers + currentIndex + question order on every change.
  useEffect(() => {
    try {
      if (typeof window === "undefined" || !window.localStorage) return;
      const answeredCount = Object.keys(answers).length;
      if (answeredCount === 0 && currentIndex === 0) return;
      window.localStorage.setItem(
        S0_PROGRESS_KEY,
        JSON.stringify({
          answers,
          currentIndex,
          questionIds: questions.map((q) => q.id),
          savedAt: Date.now(),
        })
      );
    } catch {
      /* swallow quota / privacy-mode errors */
    }
  }, [answers, currentIndex, questions]);

  const answeredCount = Object.keys(answers).length;
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const isComplete = answeredCount === questions.length;
  const progressPercent = (answeredCount / questions.length) * 100;

  const goToQuestion = useCallback((nextIndex, direction) => {
    if (nextIndex < 0 || nextIndex >= questions.length) return;
    setSlideDir(`out-${direction}`);
    setTimeout(() => {
      setCurrentIndex(nextIndex);
      setSlideDir(`in-${direction}`);
      setTimeout(() => setSlideDir("idle"), 250);
    }, 200);
  }, [questions.length]);

  const handleAnswerSelect = (rating) => {
    if (slideDir !== "idle") return;
    setAnswers((current) => ({ ...current, [currentQuestion.id]: rating }));
  };

  const handleGoBack = () => {
    if (currentIndex === 0 || slideDir !== "idle") return;
    goToQuestion(currentIndex - 1, "back");
  };

  const handleGoForward = () => {
    if (isLastQuestion || slideDir !== "idle") return;
    if (!answers[currentQuestion.id]) return;
    goToQuestion(currentIndex + 1, "forward");
  };

  const handleGetResult = useCallback(() => {
    if (!isComplete) return;
    setShowModal(true);
    setSubmitError("");
  }, [isComplete]);

  const handleModalSubmit = useCallback(async (e) => {
    e.preventDefault();
    const name = modalName.trim();
    const mobile = modalMobile.trim();
    if (!name || !mobile) return;

    // Validate name: no digits, at least 3 alphabetic characters
    if (/\d/.test(name)) {
      setSubmitError("Name cannot contain digits.");
      return;
    }
    const alphaCount = (name.match(/[A-Za-z]/g) || []).length;
    if (alphaCount < 3) {
      setSubmitError("Name must contain at least 3 alphabets.");
      return;
    }

    // Validate: exactly 10 digits
    if (!/^\d{10}$/.test(mobile)) {
      setSubmitError("Please enter a valid 10-digit mobile number.");
      return;
    }
    // Must start with 6, 7, 8, or 9
    if (!/^[6-9]/.test(mobile)) {
      setSubmitError("Mobile number must start with 6, 7, 8, or 9.");
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    const finalScores = questions.reduce((acc, q) => {
      if (!acc[q.type]) acc[q.type] = 0;
      const val = answers[q.id];
      if (val) acc[q.type] += val;
      return acc;
    }, {});

    // Build per-question answers: { "Q01": 4, "Q02": 3, ... "Q15": 5 }
    const rawAnswers = {};
    for (const q of questions) {
      const idx = parseInt(q.id.replace("question-", ""), 10);
      const code = `Q${String(idx).padStart(2, "0")}`;
      rawAnswers[code] = answers[q.id] || 0;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/s0-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: modalName.trim(),
          mobile: modalMobile.trim(),
          scores: finalScores,
          raw_answers: rawAnswers,
        }),
      });

      if (!res.ok) {
        if (res.status === 409) {
          const errData = await res.json().catch(() => null);
          throw new Error(errData?.detail || "This mobile number has already been used.");
        }
        if (res.status === 422) {
          const errData = await res.json().catch(() => null);
          const detail = errData?.detail;
          if (typeof detail === "string") throw new Error(detail);
          if (Array.isArray(detail) && detail.length > 0) {
            const field = detail[0].loc?.slice(-1)[0] || "";
            if (field === "mobile") throw new Error("Please enter a valid mobile number.");
            if (field === "name") throw new Error("Please enter your name.");
          }
        }
        throw new Error("Something went wrong. Please try again.");
      }

      const data = await res.json();
      // Meta Pixel + GA4: form submitted, report is about to be generated.
      trackCompleteRegistration();
      gaSignUp();
      // Clear in-progress answers from localStorage now that we've submitted.
      clearStoredProgress();
      navigate(`/s0-report/${data.id}`, { state: { reportData: { ...data, scores: finalScores } } });
    } catch (err) {
      setSubmitError(err.message || "Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [modalName, modalMobile, questions, answers, navigate]);

  return (
    <section className={`section-shell founder-signal-section${compact ? " founder-signal-section--compact" : ""}`} data-reveal="true" data-testid="founder-signal-section" id="founder-signal-test">
      <div className="pivotbox-container section-grid founder-signal-grid">
        {!compact && (
        <div className="span-4 founder-signal-copy" data-testid="founder-signal-copy-column">
          <span className="founder-signal-label dm-mono" data-testid="founder-signal-label">
            THE SIMPLE SOLUTION
          </span>
          <h2 className="founder-signal-title" data-testid="founder-signal-title">
            Start with a simple test.
          </h2>
          <p className="founder-signal-body founder-signal-body--lead" data-testid="founder-signal-body-paragraph-1">
            Why do you want to start a business?
          </p>
          <p className="founder-signal-body" data-testid="founder-signal-body-paragraph-2">
            For some people it is about change or frustration, for others it is about purpose, growth, or independence.
          </p>
          <p className="founder-signal-body" data-testid="founder-signal-body-paragraph-3">
            The &lsquo;Founder Signal&rsquo; test helps you discover the motivation behind your entrepreneurial thinking and reflects on your next step.
          </p>
          <p className="founder-signal-body" data-testid="founder-signal-body-paragraph-4">
            Rate 15 honest statements on a scale of 1 to 5. No right or wrong answers, just a mirror held up to where you actually are.
          </p>
        </div>
        )}

        <div className={`${compact ? "span-12" : "span-8"} founder-signal-card-column`} data-testid="founder-signal-card-column">
          <div className="signal-test-card" data-testid="founder-signal-card">
            <div className="signal-test-card__header" data-testid="founder-signal-card-header">
              <div className="signal-test-card__title" data-testid="founder-signal-card-header-title">
                Founder Signal Assessment
              </div>
              <div className="signal-test-card__counter" data-testid="founder-signal-progress-label">
                {currentIndex + 1} of {questions.length}
              </div>
            </div>

            <div className="signal-test-card__progress-track" data-testid="founder-signal-progress-track">
              <div
                className="signal-test-card__progress-fill"
                data-testid="founder-signal-progress-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <div className="signal-test-card__body" data-testid="founder-signal-card-body">
              <div className="signal-test-card__nav-row" data-testid="founder-signal-nav-buttons">
                <button
                  className={`signal-test-card__nav-btn ${answers[currentQuestion.id] && currentIndex > 0 ? "is-answered" : ""}`}
                  data-testid="founder-signal-question-back-button"
                  disabled={currentIndex === 0 || slideDir !== "idle"}
                  onClick={handleGoBack}
                  type="button"
                >
                  &larr; Back
                </button>
                <button
                  className={`signal-test-card__nav-btn ${answers[currentQuestion.id] && !isLastQuestion ? "is-answered" : ""}`}
                  data-testid="founder-signal-question-next-button"
                  disabled={isLastQuestion || slideDir !== "idle" || !answers[currentQuestion.id]}
                  onClick={handleGoForward}
                  type="button"
                >
                  Next &rarr;
                </button>
              </div>

              <div className={`signal-test-card__slide-shell ${slideDir !== "idle" ? `is-${slideDir}` : ""}`} data-testid="founder-signal-question-shell">
                <div className="signal-test-card__q-label" data-testid="founder-signal-question-count">
                  QUESTION {currentIndex + 1}
                </div>
                <p className="signal-test-card__question-text" data-testid="founder-signal-question-text">
                  {currentQuestion.text}
                </p>

                <div className="signal-test-card__answer-list" data-testid="founder-signal-rating-grid">
                  {answerLabels.map(({ value, label }) => {
                    const isSelected = answers[currentQuestion.id] === value;
                    return (
                      <button
                        className={`signal-test-card__answer-btn ${isSelected ? "is-selected" : ""}`}
                        data-testid={`founder-signal-rating-button-${value}`}
                        key={value}
                        onClick={() => handleAnswerSelect(value)}
                        type="button"
                      >
                        <span className="signal-test-card__radio" />
                        <span className="signal-test-card__answer-label">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {isLastQuestion && isComplete && (
                <div className="signal-test-card__get-result" data-testid="founder-signal-card-footer">
                  <button
                    className="signal-test-card__result-button is-active"
                    data-testid="founder-signal-result-button"
                    onClick={handleGetResult}
                    type="button"
                  >
                    Get My Result &rarr;
                  </button>
                </div>
              )}

              <div className="signal-test-card__meta" data-testid="founder-signal-card-meta">
                &#10003; Free &nbsp; &bull; &nbsp; 5 minutes &nbsp; &bull; &nbsp; No sign-up required
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile capture modal — rendered via portal to avoid transform containment */}
      {showModal && createPortal(
        <div className="s0-modal-overlay" data-testid="s0-modal-overlay" onClick={() => !submitting && setShowModal(false)}>
          <div className="s0-modal" data-testid="s0-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="s0-modal__close"
              data-testid="s0-modal-close-button"
              onClick={() => !submitting && setShowModal(false)}
              type="button"
            >
              &times;
            </button>
            <h3 className="s0-modal__title" data-testid="s0-modal-title">Almost there!</h3>
            <p className="s0-modal__desc" data-testid="s0-modal-desc">
              Enter your details to receive your personalised Founder Signal result.
            </p>
            <form onSubmit={handleModalSubmit} className="s0-modal__form" data-testid="s0-modal-form">
              <label className="s0-modal__label" data-testid="s0-modal-name-label">
                Name
                <input
                  type="text"
                  className="s0-modal__input"
                  data-testid="s0-modal-name-input"
                  placeholder="Your name (letters only, min 3)"
                  value={modalName}
                  onChange={(e) => { const v = e.target.value.replace(/\d+/g, ""); setModalName(v); }}
                  pattern="[^0-9]{3,}"
                  minLength={3}
                  required
                  disabled={submitting}
                />
              </label>
              <label className="s0-modal__label" data-testid="s0-modal-mobile-label">
                Mobile number
                <input
                  type="tel"
                  className="s0-modal__input"
                  data-testid="s0-modal-mobile-input"
                  placeholder="Your mobile number"
                  value={modalMobile}
                  onChange={(e) => { const v = e.target.value.replace(/\D/g, "").slice(0, 10); setModalMobile(v); }}
                  maxLength={10}
                  pattern="[6-9][0-9]{9}"
                  required
                  disabled={submitting}
                />
              </label>
              {submitError && (
                <div className="s0-modal__error" data-testid="s0-modal-error">{submitError}</div>
              )}
              <button
                type="submit"
                className="pivotbox-button s0-modal__submit"
                data-testid="s0-modal-submit-button"
                disabled={submitting || !modalName.trim() || !modalMobile.trim()}
              >
                {submitting ? "Generating your result..." : "See my Result"}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
